import {
  interestRateModelV2Abi,
  siloAbi,
  siloLensAbi,
} from "@silo/contract-abi-types";
import { siloLensAddress } from "@silo/contract-addresses";
import {
  Address,
  Amount,
  ChainPublicClientService,
  IrmConfig,
  makeAmount,
  makePercent,
  Percent,
  PERCENT_DECIMALS,
  PERCENT_ZERO,
  ProtocolKeyService,
} from "@silo/types";
import { Duration, Effect, pipe, BigInt as EffectBigInt } from "effect";
import { getContract } from "viem";
import { ContractsClientError } from "../types";
import { getCollateralBaseApr, shiftDecimal } from "@silo/utils";
import { match, P } from "ts-pattern";

interface MarketState {
  currentConfig: IrmConfig;
  collateralTotal: Amount;
  debtTotal: Amount;
}

export type GetAprProjections = ReturnType<typeof getAprProjections>;
export type GetAprProjectionsSuccess = Effect.Effect.Success<GetAprProjections>;
export type GetAprProjectionsError = Effect.Effect.Error<GetAprProjections>;
export type GetAprProjectionsContext = Effect.Effect.Context<GetAprProjections>;

export interface GetAprProjectionsParams {
  marketAddress: Address;
  tokenAddress: Address;
  irmAddress: Address;
  irmConfig: IrmConfig;
  protocolShareFee: Percent;
  collateralAmount: Amount;
  debtAmount: Amount;
}

export interface AprProjections {
  collateralBaseApr: Percent;
  collateralBaseApr1d: Percent;
  collateralBaseApr3d: Percent;
  debtBaseApr: Percent;
  debtBaseApr1d: Percent;
  debtBaseApr3d: Percent;
}

/**
 * The current process for making interest rate calculations is quite involved.
 * Ideally, this would eventually be abstracted in a single pure function
 * available from the contracts.
 *
 * 1) Grab the old IRM config. This will provide IRM parameters and the old
 *    values of `ri` and `Tcrit` (calculated at the time of the last transaction
 *    in the silo). The config is passed into this function as the `config` arg
 * 2) Call `calculateCompoundInterestRate` with the old utilization data and the
 *    block timestamp as the present moment. This will provide the current `ri`
 *    and `Tcrit` values that need to be used to make APR projections from the
 *    current moment.
 * 3) Override the old `ri` and `Tcrit` values in the config with the new ones.
 * 4) Grab the current `totalDeposits` and `totalBorrowAmount`, with interest
 *    accrued up to the current moment.
 * 5) Call `calculateCurrentInterestRate` with the updated config, current
 *    deposit / borrow values, the interest rate timestamp as the present
 *    moment, and the block timestamp as a current or future value. The deposit
 *    or borrow values can also be manipulated.
 */
export function getAprProjections({
  marketAddress,
  tokenAddress,
  irmAddress,
  irmConfig,
  protocolShareFee,
  collateralAmount,
  debtAmount,
}: GetAprProjectionsParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;

    const siloContract = getContract({
      address: marketAddress,
      abi: siloAbi,
      client: publicClient,
    });

    const siloLensContract = getContract({
      address: siloLensAddress[protocolKey],
      abi: siloLensAbi,
      client: publicClient,
    });

    const irmContract = getContract({
      address: irmAddress,
      abi: interestRateModelV2Abi,
      client: publicClient,
    });

    const currentDuration = pipe(Date.now(), Duration.millis);
    const currentTimestamp = durationToSeconds(currentDuration);

    const marketState = yield* Effect.tryPromise({
      try: async () => {
        async function getCurrentConfig(): Promise<IrmConfig> {
          const data = await siloContract.read.utilizationData([tokenAddress]);

          const [, ri, Tcrit] =
            await irmContract.read.calculateCompoundInterestRate([
              irmConfig,
              data.totalDeposits,
              data.totalBorrowAmount,
              data.interestRateTimestamp,
              currentTimestamp,
            ]);

          const currentConfig: IrmConfig = {
            ...irmConfig,
            ri,
            Tcrit,
          };

          return currentConfig;
        }

        const [currentConfig, collateralTotal, debtTotal] = await Promise.all([
          getCurrentConfig(),
          siloLensContract.read
            .totalDepositsWithInterest([marketAddress, tokenAddress])
            .then(makeAmount),
          siloLensContract.read
            .totalBorrowAmountWithInterest([marketAddress, tokenAddress])
            .then(makeAmount),
        ]);

        const marketState: MarketState = {
          currentConfig,
          collateralTotal,
          debtTotal,
        };

        return marketState;
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    const blockTimestamp = currentTimestamp;

    const blockTimestamp1d = pipe(
      currentDuration,
      Duration.sum(Duration.days(1)),
      durationToSeconds
    );

    const blockTimestamp3d = pipe(
      currentDuration,
      Duration.sum(Duration.days(3)),
      durationToSeconds
    );

    //console.log({ blockTimestamp, blockTimestamp1d, blockTimestamp3d });

    function getProjectionsForTimestamp(blockTimestamp: bigint) {
      return Effect.gen(function* () {
        const { currentConfig, collateralTotal, debtTotal } = marketState;

        const newCollateralTotal = pipe(
          collateralTotal + collateralAmount,
          makeAmount
        );

        /**
         * Clamp the APR projection between 0% and 100% utilization. Otherwise, the user could
         * enter an outrageous amount in the borrow input and cause the calculation
         * to enter an overflow state, which would show up as 0 in the UI.
         */
        const newDebtTotal = pipe(
          debtTotal + debtAmount,
          EffectBigInt.clamp({
            minimum: 0n,
            maximum: collateralTotal,
          }),
          makeAmount
        );

        const debtBaseApr = yield* Effect.tryPromise({
          try: async () => {
            return irmContract.read
              .calculateCurrentInterestRate([
                currentConfig,
                newCollateralTotal,
                newDebtTotal,
                currentTimestamp,
                blockTimestamp,
              ])
              .then(makePercent);
          },
          catch: (cause) => new ContractsClientError({ cause }),
        });

        const collateralBaseApr = match(newCollateralTotal)
          .with(P.bigint.gt(0n), () => {
            // https://github.com/silo-finance/silo-contracts/blob/develop/contracts/SiloLens.sol#L341
            const generatedDebtAmount = shiftDecimal(
              newDebtTotal * debtBaseApr,
              -PERCENT_DECIMALS
            );

            const rawCollateralBaseApr = pipe(
              shiftDecimal(generatedDebtAmount, PERCENT_DECIMALS) /
                newCollateralTotal,
              makePercent
            );

            return getCollateralBaseApr(protocolShareFee, rawCollateralBaseApr);
          })
          .otherwise(() => PERCENT_ZERO);

        return [collateralBaseApr, debtBaseApr] as const;
      });
    }

    const [
      [collateralBaseApr, debtBaseApr],
      [collateralBaseApr1d, debtBaseApr1d],
      [collateralBaseApr3d, debtBaseApr3d],
    ] = yield* Effect.all(
      [
        getProjectionsForTimestamp(blockTimestamp),
        getProjectionsForTimestamp(blockTimestamp1d),
        getProjectionsForTimestamp(blockTimestamp3d),
      ],
      { concurrency: "unbounded" }
    );

    const aprProjections: AprProjections = {
      collateralBaseApr,
      collateralBaseApr1d,
      collateralBaseApr3d,
      debtBaseApr,
      debtBaseApr1d,
      debtBaseApr3d,
    };

    return aprProjections;
  });
}

function durationToSeconds(duration: Duration.Duration): bigint {
  return pipe(duration, Duration.toSeconds, Math.round, BigInt);
}
